import { useMemo } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

// ── Colours ───────────────────────────────────────────────────────────────────

const PHASE_COLORS = ["#6366f1","#06b6d4","#f59e0b","#10b981","#ec4899","#8b5cf6","#f97316","#14b8a6"];

const STATUS_META = {
  Open:            { label: "Open",             color: "#94a3b8" },
  InProgress:      { label: "In Progress",      color: "#3b82f6" },
  UnderValidation: { label: "Under Validation", color: "#f59e0b" },
  Hold:            { label: "On Hold",          color: "#f97316" },
  Completed:       { label: "Completed",        color: "#22c55e" },
};

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = "var(--text)" }) {
  return (
    <div style={{ flex: "1 1 140px", padding: "16px 20px", borderRadius: 12, background: "var(--surface)", border: "1px solid var(--border)", minWidth: 120 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px 24px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
      {label && <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--text)" }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || "var(--text-2)" }}>{p.name}: <strong>{p.value}</strong></div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProjectDashboardView({ project, tasks, phases }) {
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === "Completed").length;
  const inProgress = tasks.filter(t => t.status === "InProgress").length;
  const atRisk = tasks.filter(t => t.atRisk).length;
  const overdue = tasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "Completed").length;
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // ── Tasks by phase (pie) — count always; effort % if available ───────────
  const hasEffortData = tasks.some(t => t.resourceAllocationPct != null && t.resourceAllocationPct > 0);

  const phaseChartData = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      const key = t.phaseName || "Unphased";
      if (!map[key]) map[key] = { count: 0, effort: 0 };
      map[key].count += 1;
      map[key].effort += t.resourceAllocationPct || 0;
    }
    return Object.entries(map)
      .map(([name, d]) => ({ name, value: hasEffortData ? d.effort : d.count, count: d.count, effort: d.effort }))
      .filter(d => d.value > 0);
  }, [tasks, hasEffortData]);

  // ── Tasks by assignee (bar) — count always; effort % if available ─────────
  const assigneeChartData = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      const key = t.assignedToUserName || "Unassigned";
      if (!map[key]) map[key] = { count: 0, effort: 0 };
      map[key].count += 1;
      map[key].effort += t.resourceAllocationPct || 0;
    }
    return Object.entries(map)
      .map(([name, d]) => ({ name: name.split(" ")[0], fullName: name, value: hasEffortData ? d.effort : d.count, count: d.count, effort: d.effort }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.value - a.value);
  }, [tasks, hasEffortData]);

  // ── Status breakdown (bar) ────────────────────────────────────────────────
  const statusData = useMemo(() => {
    const map = {};
    for (const t of tasks) { map[t.status] = (map[t.status] || 0) + 1; }
    return Object.entries(STATUS_META)
      .map(([key, meta]) => ({ name: meta.label, count: map[key] || 0, color: meta.color }))
      .filter(d => d.count > 0);
  }, [tasks]);

  // ── Phase progress table ──────────────────────────────────────────────────
  const phaseProgress = useMemo(() => {
    const rows = phases.map(ph => {
      const pts = tasks.filter(t => t.phaseId === ph.id);
      const done = pts.filter(t => t.status === "Completed").length;
      const risk = pts.filter(t => t.atRisk).length;
      const pct = pts.length > 0 ? Math.round((done / pts.length) * 100) : 0;
      return { name: ph.name, total: pts.length, done, risk, pct };
    });
    // Unphased
    const unphased = tasks.filter(t => !t.phaseId);
    if (unphased.length > 0) {
      const done = unphased.filter(t => t.status === "Completed").length;
      rows.push({ name: "Unphased", total: unphased.length, done, risk: unphased.filter(t => t.atRisk).length, pct: Math.round((done / unphased.length) * 100) });
    }
    return rows;
  }, [tasks, phases]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── KPI Cards ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatCard label="Total Tasks" value={total} />
        <StatCard label="Completed" value={completed} sub={`${progressPct}% done`} color="#16a34a" />
        <StatCard label="In Progress" value={inProgress} color="#2563eb" />
        <StatCard label="At Risk" value={atRisk} color={atRisk > 0 ? "#dc2626" : "var(--text)"} />
        <StatCard label="Overdue" value={overdue} color={overdue > 0 ? "#dc2626" : "var(--text)"} />
      </div>

      {/* ── Overall progress bar ── */}
      {total > 0 && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            <span>Overall Progress</span>
            <span style={{ color: progressPct >= 70 ? "#16a34a" : progressPct >= 30 ? "#b45309" : "#dc2626" }}>{progressPct}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 6, background: "var(--border-strong)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progressPct}%`, background: progressPct >= 70 ? "#22c55e" : progressPct >= 30 ? "#f59e0b" : "#ef4444", borderRadius: 6, transition: "width 0.4s ease" }} />
          </div>
        </div>
      )}

      {/* ── Charts row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Tasks by phase — pie */}
        <Section title={hasEffortData ? "Effort % by Phase" : "Tasks by Phase"}>
          {phaseChartData.length === 0 ? (
            <div style={{ color: "var(--text-3)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No tasks yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={phaseChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`} labelLine={false} fontSize={11}>
                  {phaseChartData.map((_, i) => <Cell key={i} fill={PHASE_COLORS[i % PHASE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} formatter={(v, _, p) => [hasEffortData ? `${p.payload.effort}%` : `${p.payload.count} tasks`, p.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Section>

        {/* Tasks by assignee — bar */}
        <Section title={hasEffortData ? "Effort % by Assignee" : "Tasks by Assignee"}>
          {assigneeChartData.length === 0 ? (
            <div style={{ color: "var(--text-3)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No tasks assigned yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={assigneeChartData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-3)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-3)" }} unit={hasEffortData ? "%" : ""} />
                <Tooltip content={<CustomTooltip />} formatter={(v, _, p) => [hasEffortData ? `${p.payload.effort}%` : `${p.payload.count} tasks`, p.payload.fullName]} />
                <Bar dataKey="value" name={hasEffortData ? "Effort" : "Tasks"} radius={[4, 4, 0, 0]}>
                  {assigneeChartData.map((_, i) => <Cell key={i} fill={PHASE_COLORS[i % PHASE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>

      {/* ── Status breakdown ── */}
      <Section title="Task Status Breakdown">
        {total === 0 ? (
          <div style={{ color: "var(--text-3)", fontSize: 13, textAlign: "center", padding: "16px 0" }}>No tasks yet.</div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {statusData.map(d => (
              <div key={d.name} style={{ flex: "1 1 100px", padding: "12px 16px", borderRadius: 10, background: `${d.color}14`, border: `1px solid ${d.color}40`, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: d.color }}>{d.count}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{d.name}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Phase progress table ── */}
      {phaseProgress.length > 0 && (
        <Section title="Phase Progress">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {phaseProgress.map(ph => (
              <div key={ph.name}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{ph.name}</span>
                    {ph.risk > 0 && <span style={{ fontSize: 11, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecdd3", borderRadius: 4, padding: "1px 6px" }}>⚑ {ph.risk} at risk</span>}
                  </div>
                  <span style={{ fontSize: 12, color: "var(--text-3)" }}>{ph.done}/{ph.total} · <strong style={{ color: ph.pct >= 70 ? "#16a34a" : ph.pct >= 30 ? "#b45309" : "#dc2626" }}>{ph.pct}%</strong></span>
                </div>
                <div style={{ height: 6, borderRadius: 4, background: "var(--border-strong)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${ph.pct}%`, background: ph.pct >= 70 ? "#22c55e" : ph.pct >= 30 ? "#f59e0b" : "#ef4444", borderRadius: 4, transition: "width 0.4s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

    </div>
  );
}
